import { isEmpty } from 'mc-ui-utils';
import { useCallback, useEffect, useRef, useState, } from 'react';
import State from '../state';
import { toggleProgressBar } from '../utils/progressbarhelper';
import { onMetaDataLoadCb } from '../utils/videohelper';
export const useVideoListeners = (videoRef, glanceId, shouldPreload, isActiveGlance) => {
    const startTime = useRef();
    //time elapsed between the start of a glance and user clicking on play button
    const playCallDuration = useRef();
    const playStartDuration = useRef(null);
    const loaded = useRef(Date.now());
    const loadingTime = useRef(Date.now());
    const videoErrorCount = useRef();
    const [isReady, toggleReady] = useState(true);
    const [hasStartedPlaying, setStartedPlaying] = useState(false);
    /**
     * Playback has stopped because of a temporary lack of data
     */
    const onBuffering = useCallback(() => {
        toggleReady(false);
        toggleProgressBar(glanceId, 'paused');
    }, [glanceId]);
    const onPlayingShaka = useCallback(() => {
        toggleReady(true);
        toggleProgressBar(glanceId, 'running');
    }, [glanceId]);
    /**
     * Playback is ready to start after having been paused or delayed due to lack of data.
     */
    const onPlaying = useCallback(() => {
        toggleReady(true);
        setStartedPlaying(true);
        onMetaDataLoadCb(videoRef.current, glanceId, isActiveGlance.current);
        toggleProgressBar(glanceId, 'running');
    }, [glanceId]);
    /**
     * The first frame of the media has finished loading.
     */
    const onLoadData = useCallback(() => {
        const videoPlayer = videoRef.current;
        const videoTimeline = State.getVideoTimeline(glanceId);
        if (videoTimeline && videoPlayer) {
            startTime.current = videoTimeline;
            videoPlayer.currentTime = videoTimeline;
        }
        if (videoPlayer &&
            videoPlayer.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
            isActiveGlance.current) {
            if (videoPlayer.paused) {
                const playPromise = videoPlayer.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                        // Automatic playback started!
                        playStartDuration.current = Date.now() - loaded.current;
                    })
                        .catch(() => {
                        playStartDuration.current = null; // this means play never started.
                    });
                }
            }
            else {
                playStartDuration.current = Date.now() - loaded.current;
            }
            playCallDuration.current = Date.now() - loaded.current;
        }
        else if (!videoPlayer.paused) {
            videoPlayer.pause();
        }
    }, [glanceId, videoRef]);
    /**
     * The metadata has been loaded.
     */
    const onMetaDataLoad = useCallback(() => {
        const videoPlayer = videoRef.current;
        // onMetaDataLoadCb(videoPlayer, glanceId, isActiveGlance.current);
        loadingTime.current = Date.now() - loaded.current;
    }, [glanceId]);
    const onError = useCallback(() => {
        videoErrorCount.current = videoErrorCount.current + 1;
    }, []);
    useEffect(() => {
        const player = videoRef.current;
        if (shouldPreload && !isEmpty(player)) {
            player.addEventListener('error', onError);
            player.addEventListener('waiting', onBuffering);
            player.addEventListener('playing', onPlaying);
            player.addEventListener('loadeddata', onLoadData); // The first frame of the media has finished loading.
            player.addEventListener('loadedmetadata', onMetaDataLoad);
        }
        return () => {
            if (!isEmpty(player)) {
                player.removeEventListener('error', onError);
                player.removeEventListener('waiting', onBuffering);
                player.removeEventListener('playing', onPlaying);
                player.removeEventListener('loadeddata', onLoadData);
                player.removeEventListener('loadedmetadata', onMetaDataLoad);
            }
        };
    }, [onBuffering, onPlaying, shouldPreload, onLoadData]);
    return {
        isReady,
        hasStartedPlaying,
        startTime,
        loaded,
        loadingTime,
        playCallDuration,
        playStartDuration,
        videoErrorCount,
        onBuffering,
        onPlayingShaka,
    };
};
//# sourceMappingURL=useVideoListeners.js.map